/**
 * The Workspace opener (contracts-plan.md §11 tab 4, §8.2). Shown when
 * `/contracts/workspace` carries neither `?workspaceId=` nor
 * `?versionId=&mode=` (WorkspaceTab.tsx's routing, this WP's brief:
 * "Route and version selection"). Study -> Negotiation -> Version -> mode
 * radio (never defaulted — G3, the two modes build different workbooks) ->
 * Open.
 *
 * [OWNER] (this WP's report NOTES): the Studies tab does not link here yet
 * (`grep -rn "contracts/workspace" frontend/src` finds nothing before this
 * WP) — the one-line link `/contracts/workspace?versionId=<id>&mode=edit_in_place`
 * belongs in the Studies tab's Versions panel, outside this WP's paths.
 */
import { useEffect, useState } from 'react';
import { LayoutGrid } from 'lucide-react';
import { PageHeader, EmptyState, FilterSelect, primaryBtnCls } from '../../../ui';
import { listStudies, listNegotiations } from '../../api/client';
import type { Study, Negotiation } from '../../api/client';
import { listNegotiationVersions, openWorkspace, describeWorkspaceApiError, type ContractsVersion, type WorkspaceMode } from '../../api/workspace';

interface Props {
  onOpened: (workspaceId: string) => void;
}

export function WorkspaceOpener({ onOpened }: Props) {
  const [studies, setStudies] = useState<Study[]>([]);
  const [studyId, setStudyId] = useState('');
  const [negotiations, setNegotiations] = useState<Negotiation[]>([]);
  const [negotiationId, setNegotiationId] = useState('');
  const [versions, setVersions] = useState<ContractsVersion[]>([]);
  const [versionId, setVersionId] = useState('');
  const [mode, setMode] = useState<WorkspaceMode | ''>('');
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listStudies().then(setStudies).catch(() => setStudies([]));
  }, []);

  useEffect(() => {
    if (!studyId) {
      setNegotiations([]);
      return;
    }
    listNegotiations(studyId).then(setNegotiations).catch(() => setNegotiations([]));
  }, [studyId]);

  useEffect(() => {
    if (!negotiationId) {
      setVersions([]);
      return;
    }
    listNegotiationVersions(negotiationId).then(setVersions).catch(() => setVersions([]));
  }, [negotiationId]);

  async function handleOpen() {
    if (!versionId || !mode) return;
    setOpening(true);
    setError(null);
    try {
      const result = await openWorkspace({ versionId, mode });
      onOpened(result.workspace.id);
    } catch (err) {
      setError(describeWorkspaceApiError('POST', '/api/contracts/workspaces', err));
    } finally {
      setOpening(false);
    }
  }

  return (
    <div className="mx-auto w-full max-w-[1700px] space-y-4 p-4 sm:p-6 lg:p-8">
      <PageHeader title="Workspace" icon={LayoutGrid} subtitle="Open a version to start working it (§8.2)." />

      <div data-testid="ws-opener" className="flex flex-wrap items-end gap-2">
        <FilterSelect
          label="Study"
          auto
          value={studyId}
          onChange={(v) => {
            setStudyId(v);
            setNegotiationId('');
            setVersionId('');
          }}
          options={[{ value: '', label: 'Choose a study…' }, ...studies.map((s) => ({ value: s.id, label: s.protocol_number }))]}
        />
        <FilterSelect
          label="Negotiation"
          auto
          value={negotiationId}
          disabled={!studyId}
          onChange={(v) => {
            setNegotiationId(v);
            setVersionId('');
          }}
          options={[{ value: '', label: 'Choose a negotiation…' }, ...negotiations.map((n) => ({ value: n.id, label: n.label ?? n.kind }))]}
        />
        <FilterSelect
          label="Version"
          auto
          value={versionId}
          disabled={!negotiationId}
          onChange={setVersionId}
          options={[
            { value: '', label: 'Choose a version…' },
            ...versions.map((v) => ({ value: v.id, label: `#${v.version_no} — ${v.direction} — ${v.version_date}` })),
          ]}
        />
        <FilterSelect
          label="Mode"
          auto
          value={mode}
          disabled={!versionId}
          onChange={(v) => setMode(v as WorkspaceMode)}
          options={[
            { value: '', label: 'Choose a mode…' },
            { value: 'edit_in_place', label: 'Edit in place' },
            { value: 'own_version', label: "WCR's own version" },
          ]}
        />
        <button
          type="button"
          data-testid="ws-opener-open"
          className={primaryBtnCls}
          disabled={!versionId || !mode || opening}
          onClick={handleOpen}
        >
          Open
        </button>
      </div>

      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

      {negotiationId && versions.length === 0 && (
        <EmptyState icon={LayoutGrid} title="No versions yet" hint="This negotiation has no documents to work." variant="card" />
      )}
    </div>
  );
}

export default WorkspaceOpener;
